import { RefObject, useCallback, useEffect, useRef, useState } from 'react';
import {
  OAI_IFRAME_EVENTS,
  OAIInboundMessage,
  OAIOutboundMessage,
  OAIMode,
  getOAITargetOrigin,
  OAIInboundHandlers,
} from '../lib/util/oai';
import { devLog } from '@osmosis/nextjs-components/util/devLog';

type UseOAIIframeChannelArgs = {
  iframeRef: RefObject<HTMLIFrameElement | null>;
  iframeSrc: string | null;
  onIframeReady?: () => void;
  onPromptSubmitted?: () => void;
  onLearnPageClick?: (message: OAIInboundMessage) => void;
};

type OAIIframeChannel = {
  isReady: boolean;
  updatePageContext: (pageContext: string) => void;
  sendUserMessage: (message: string, mode?: OAIMode) => void;
};

/**
 * Custom hook to manage communication with the OAI iframe via postMessage.
 */
export const useOAIIframeChannel = ({
  iframeRef,
  iframeSrc,
  onIframeReady,
  onPromptSubmitted,
  onLearnPageClick,
}: UseOAIIframeChannelArgs): OAIIframeChannel => {
  const [isReady, setIsReady] = useState(false);

  // Updated synchronously each render so the stable listener always sees the
  // latest callbacks without a stale-closure problem.
  const handlersRef = useRef<OAIInboundHandlers>({});

  handlersRef.current = {
    [OAI_IFRAME_EVENTS.IframeReady]: () => {
      setIsReady(true);
      onIframeReady?.();
    },
    [OAI_IFRAME_EVENTS.PromptSubmitted]: () => {
      onPromptSubmitted?.();
    },
    [OAI_IFRAME_EVENTS.LearnPageClick]: (data) => {
      onLearnPageClick?.(data);
    },
  };

  // Reset handshake whenever the iframe src changes (new iframe = new READY cycle).
  useEffect(() => {
    setIsReady(false);
  }, [iframeSrc]);

  useEffect(() => {
    if (typeof window === 'undefined') return undefined;

    const handler = (event: MessageEvent<OAIInboundMessage>) => {
      const expectedOrigin = getOAITargetOrigin(iframeSrc);

      if (expectedOrigin === '*' || event.origin !== expectedOrigin) return;

      const type = event?.data?.type;
      if (!type) return;

      devLog('[OAI Iframe] Received message', event.data);

      handlersRef.current[type]?.(event.data);
    };

    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [iframeSrc]);

  const postMessage = useCallback(
    (message: OAIOutboundMessage): void => {
      devLog('[OAI Iframe] Sending message', message);
      iframeRef.current?.contentWindow?.postMessage(
        message,
        getOAITargetOrigin(iframeSrc),
      );
    },
    [iframeRef, iframeSrc],
  );

  const updatePageContext = useCallback(
    (pageContext: string): void => {
      if (!isReady) return;
      postMessage({ type: OAI_IFRAME_EVENTS.PageContextUpdate, pageContext });
    },
    [isReady, postMessage],
  );

  const sendUserMessage = useCallback(
    (message: string, mode: OAIMode = OAIMode.DefaultStudy): void => {
      if (!isReady) return;
      postMessage({
        type: OAI_IFRAME_EVENTS.SubmitUserMessage,
        data: { message, mode },
      });
    },
    [isReady, postMessage],
  );

  return { isReady, updatePageContext, sendUserMessage };
};

export default useOAIIframeChannel;
